import { PagedResult, rqlBuilder, StaffEnlistment, UserConfiguration } from '@extrahorizon/javascript-sdk';
import { chunk, omit, orderBy, set } from 'lodash';
import { sdk, RAW_ENDPOINTS, assertAffectedRecord, queryParams } from 'utils/query';
import { snakeCaseKeys } from 'utils/helpers';
import { GroupReportConfiguration } from 'models/report';
import { TableInput } from 'models/general';
import { GroupDetail, GroupEnlistment } from 'models/groups';
import { Indicator } from 'utils/models';
import {
  getCurrentUserConfiguration,
  getGroupConfiguration,
  getUserConfigurationById,
  updateGroupConfiguration,
} from './configuration';
import { getMe } from './user';
import { KeepGroupVisibleError } from './errors/KeepGroupVisibleError';
import { EmptyParametersError } from './errors/EmptyParametersError';
import { ElementNotFoundError } from './errors/ElementNotFoundError';
import { showUsaFeature } from '../utils/app';

const getHiddenGroups = async (userConfiguration?: UserConfiguration) => {
  let configuration = userConfiguration;
  if (!userConfiguration) {
    configuration = await getCurrentUserConfiguration();
  }

  if (configuration?.data?.hiddenGroups) {
    if (Array.isArray(configuration.data.hiddenGroups)) {
      return configuration.data.hiddenGroups;
    }
    const hiddenGroups = JSON.parse(configuration.data.hiddenGroups);
    return hiddenGroups as string[];
  }

  return [];
};

export const updateGroupVisibility = async (variables: {
  groupId: string;
  visible: boolean;
  visibleGroupsIds?: string[];
}) => {
  const { groupId, visible, visibleGroupsIds } = variables;
  if (!groupId || !visibleGroupsIds) throw new EmptyParametersError();

  if (!visible && visibleGroupsIds.length === 1) {
    return new KeepGroupVisibleError();
  }

  const user = await getMe();
  const hiddenGroups = await getHiddenGroups();
  let updatedHiddenGroups = hiddenGroups;

  if (visible) {
    updatedHiddenGroups = hiddenGroups.filter((v) => v !== groupId);
  } else {
    updatedHiddenGroups = Array.from(new Set([...hiddenGroups, groupId]));
  }

  const result = await sdk.configurations.users.update(user.id, {
    data: {
      hidden_groups: JSON.stringify(updatedHiddenGroups),
    },
  });

  return assertAffectedRecord({ result });
};

export const getGroups = async (
  staffEnlistments?: StaffEnlistment[],
  userConfiguration?: UserConfiguration,
  limit = 20,
) => {
  const ids = staffEnlistments?.map((enlistment) => enlistment.groupId);
  const sliceIds = chunk(ids, limit);

  let groups: GroupEnlistment[] = [];

  for (const groupIds of sliceIds) {
    const { data: newData } = await sdk.raw.get<PagedResult<GroupEnlistment>>(
      `${RAW_ENDPOINTS.GROUPS}/?in(id,${groupIds.toString()})&select(id,name,description)&sort(name)`,
    );
    groups = groups.concat(newData.data);
  }

  const hiddenGroups = await getHiddenGroups(userConfiguration);

  const groupsWithHiddenField = groups.map((group) => ({
    ...group,
    hidden: hiddenGroups.includes(group.id),
  }));

  const visibleGroups = groupsWithHiddenField.filter((group) => !group.hidden);

  if (visibleGroups.length === 0 && groups.length > 0) {
    // set first group always visible when none is visible
    const groupId = groups[0].id;
    await updateGroupVisibility({
      groupId,
      visible: true,
      visibleGroupsIds: [groupId],
    });
    return groupsWithHiddenField.map((item) => (item.id === groupId ? { ...item, hidden: false } : item));
  }

  return groupsWithHiddenField;
};

export const getGroup = async (groupId?: string): Promise<GroupDetail> => {
  if (!groupId) throw new EmptyParametersError();

  const rql = rqlBuilder().eq('id', groupId).build();
  const { data: result } = await sdk.raw.get<PagedResult<GroupDetail>>(`${RAW_ENDPOINTS.GROUPS}/${rql}`);

  if (result.data.length !== 1) throw new ElementNotFoundError();

  const { patientConfiguration } = await getGroupConfiguration(groupId);

  const { id, name, description, address } = result.data[0];

  return {
    id,
    name,
    description,
    address: {
      addressLine1: address?.addressLine1,
      addressLine2: address?.addressLine2,
      city: address?.city,
      postalCode: address?.postalCode,
      country: address?.country,
    },
    patientConfiguration: {
      mainContactEmail: patientConfiguration?.mainContactEmail,
      mainContactLanguage: patientConfiguration?.mainContactLanguage,
      bloodPressure: patientConfiguration?.bloodPressure || { enabled: false },
      weightBmi: patientConfiguration?.weightBmi || { enabled: false },
    },
  };
};

export const updateGroup = async (variables: GroupDetail) => {
  const { id, name, description, address, patientConfiguration } = variables;

  await sdk.raw.put(`${RAW_ENDPOINTS.GROUPS}/${id}`, snakeCaseKeys({ name, description, address }));

  return await updateGroupConfiguration({ id, patientConfiguration });
};

export const getGroupReportConfiguration = async (groupId?: string) => {
  if (!groupId) throw new EmptyParametersError();
  const configuration = await sdk.raw.get<GroupReportConfiguration>(
    `${RAW_ENDPOINTS.REPORTS}/configurations/${groupId}`,
  );

  /* For the US, we hijack the quality measurement type to store the inconclusive measurement type. The reason is:
   * 1. The report service doesn't support the inconclusive measurement type (and Exh doesn't maintain report service anymore)
   * 2. Bad quality measurements are removed from reports so there's no point in configuring anything for them
   * All reports are finalized by finalize-reports task and it will put the right measurements in
   */

  const reportContent = showUsaFeature
    ? set(
        omit(configuration.data.reportContent, Indicator.Quality),
        Indicator.Inconclusive,
        configuration.data.reportContent.quality,
      )
    : configuration.data.reportContent;
  return { ...configuration.data, reportContent };
};

export const updateGroupReportConfiguration = async (variables: GroupReportConfiguration) => {
  const { id, prescriptionTriggers, reportContent } = variables;

  /* For the US, we hijack the quality measurement type to store the inconclusive measurement type. The reason is:
   * 1. The report service doesn't support the inconclusive measurement type (and Exh doesn't maintain report service anymore)
   * 2. Bad quality measurements are removed from reports so there's no point in configuring anything for them
   * All reports are finalized by finalize-reports task and it will put the right measurements in
   */

  const newContent = showUsaFeature
    ? set(omit(reportContent, Indicator.Inconclusive), Indicator.Quality, reportContent.inconclusive)
    : reportContent;

  const update = { reportContent: { ...newContent }, prescriptionTriggers: { ...prescriptionTriggers } };

  await sdk.raw.put(`${RAW_ENDPOINTS.REPORTS}/configurations/${id}`, snakeCaseKeys(update));
};

export const getGroupSettings = async ({ currentUser, filters, config, limit = 20 }: TableInput) => {
  if (!currentUser) throw new EmptyParametersError();
  const sort = filters.sort || config.defaultSort || '';
  const userConfiguration = await getUserConfigurationById(currentUser.user.id);

  let groups = await getGroups(currentUser.user.staffEnlistments, userConfiguration, limit);

  if (filters[queryParams.name]) {
    groups = groups.filter((group) => group.name.toLocaleLowerCase().includes(filters[queryParams.name].toLowerCase()));
  }

  if (sort.includes(queryParams.name)) {
    groups = orderBy(groups, 'name', sort === '-name' ? 'desc' : 'asc');
  }

  const result: PagedResult<GroupEnlistment> = {
    page: {
      total: groups.length,
      offset: groups.length,
      limit,
    },
    data: groups,
  };
  return result;
};
